import type { ChangeEvent } from 'react'
import Slider from 'react-slider'
import React, { forwardRef, useEffect, useRef, useState } from 'react'
import moduleStyles from './PriceRangeSlider.module.scss'
import type { RangeSliderValue } from '@ui/components/RangeSlider/RangeSlider'
import { Section } from '@components/category/CategorySidePanel/Section'
import { CwsP, CwsGridRow, CwsGridCol, CwsSpan } from '@components/cws'
import { Input } from '@ui/components'
import { useTranslate } from '@hooks/useTranslation'
import { startTypingTimer } from '@utils/startTypingTimer'
import { useCurrency } from '@hooks/useCurrency'
import { getFormattedPrice } from '@utils/price'
import { useRouter } from 'next/router'

interface SliderProps {
  minAvailablePrice: string
  maxAvailablePrice: string
  currentMinPrice: number
  currentMaxPrice: number
  minValue: number
  maxValue: number
  resetMin: boolean
  resetMax: boolean
  onChange: (value: RangeSliderValue) => void
}

export const PriceRangeSlider = forwardRef<HTMLElement, SliderProps>(
  ({ minAvailablePrice, maxAvailablePrice, currentMaxPrice, currentMinPrice, onChange, minValue, maxValue }, ref) => {
    const gap = 0
    const { currency } = useCurrency()
    const {
      query: { slug = '' },
    } = useRouter()
    const minRef = useRef(minAvailablePrice)
    const maxRef = useRef('0')

    const isReset = currentMinPrice === 0 && currentMaxPrice === 0

    const [sliderValues, setSliderValues] = useState<number[]>([
      +(isReset && Number.isFinite(minValue) ? minValue : +minAvailablePrice),
      +(isReset && Number.isFinite(maxValue) ? maxValue : +maxAvailablePrice),
    ])
    const { translate } = useTranslate()

    useEffect(() => {
      const min = Number.isFinite(currentMinPrice) && currentMinPrice !== 0 ? +currentMinPrice : +minAvailablePrice
      const max = Number.isFinite(currentMaxPrice) && currentMaxPrice !== 0 ? +currentMaxPrice : +maxAvailablePrice
      setSliderValues([min, max])

      minRef.current = minAvailablePrice
      maxRef.current = maxAvailablePrice
    }, [slug, isReset, currentMinPrice, currentMaxPrice, minAvailablePrice, maxAvailablePrice])

    const [sliderOneValueText, setSliderOneValueText] = useState<string | null>(null)
    const [sliderTwoValueText, setSliderTwoValueText] = useState<string | null>(null)

    const handleChangeInputMinValue = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      if (isNaN(parseFloat(value)) && value !== '') return
      setSliderOneValueText(value)
      if (parseFloat(value) > parseFloat(minRef.current) && parseFloat(value) < sliderValues[1]) {
        startTypingTimer(() => {
          setSliderValues((prevState) => [+event.target.value, prevState[1]])
        })
      } else {
        startTypingTimer(() => {
          setSliderOneValueText(sliderValues[0].toString())
        })
      }
    }
    const handleChangeInputMaxValue = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      if (isNaN(parseFloat(value)) && value !== '') return
      setSliderTwoValueText(event.target.value)
      if (parseFloat(value) < parseFloat(maxRef.current) && parseFloat(value) > sliderValues[0]) {
        startTypingTimer(() => {
          setSliderValues((prevState) => [prevState[0], +event.target.value])
        })
      } else {
        startTypingTimer(() => {
          setSliderTwoValueText(sliderValues[1].toString())
        })
      }
    }
    useEffect(() => {
      onChange({ min: sliderValues[0], max: sliderValues[1] })
    }, [sliderValues])

    return (
      <Section title={translate('filters.price') as string} open>
        <CwsGridRow>
          <CwsGridCol col="12">
            <CwsP className="cws-m-0">
              {`${translate('w2.search.filter.pricerange.from')} `}
              <CwsSpan weight="700">{getFormattedPrice(+(sliderValues[0] || minAvailablePrice), currency)}</CwsSpan>
              {` ${translate('w2.search.filter.pricerange.to')} `}
              <CwsSpan weight="700">{getFormattedPrice(+(sliderValues[1] || maxAvailablePrice), currency)}</CwsSpan>
            </CwsP>
          </CwsGridCol>
          <CwsGridCol col="12">
            <Slider
              className={moduleStyles.slider}
              onChange={(value) => {
                setSliderValues(value)
              }}
              max={+maxAvailablePrice}
              min={+minAvailablePrice}
              value={sliderValues}
            />
          </CwsGridCol>
          <CwsGridCol col="12">
            <CwsGridRow justifyContent="space-between">
              <CwsGridCol col="auto">
                <Input
                  value={sliderOneValueText ?? sliderValues[0].toString()}
                  className="text-center text-sm w-full"
                  wrapperClassName="h-[32px]"
                  onChange={(newValue, event) => handleChangeInputMinValue(event)}
                />
              </CwsGridCol>
              <CwsGridCol col="auto">
                <Input
                  value={sliderTwoValueText ?? sliderValues[1].toString()}
                  className="text-center text-sm w-full"
                  wrapperClassName="h-[32px]"
                  onChange={(newValue, event) => handleChangeInputMaxValue(event)}
                />
              </CwsGridCol>
            </CwsGridRow>
          </CwsGridCol>
        </CwsGridRow>
      </Section>
    )
  }
)
